import { useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import TagInput from "@/components/TagInput";
import { formatApiErrorDetail } from "@/lib/api";
import { ArrowRight, MapPin, Repeat, Wallet, Loader2, Info, AtSign } from "lucide-react";
import { PasswordInput } from "@/components/PasswordInput";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";

export default function AuthPage() {
  const { login, register, forgotPassword } = useAuth();
  const { t, lang } = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState(location.pathname === "/registrati" ? "register" : "login");
  const refParam = new URLSearchParams(location.search).get("ref") || localStorage.getItem("deuro_ref") || "";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAbout, setShowAbout] = useState(false);
  const [resetLink, setResetLink] = useState("");
  const [form, setForm] = useState({
    email: "", password: "", name: "", username: "", user_type: "", plan: 5, city: "", bio: "", lat: null, lng: null,
    skills: [], services: [], products: [], category: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const useLocationBtn = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => { set("lat", pos.coords.latitude); set("lng", pos.coords.longitude); },
      () => setError("—")
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "register" && !form.user_type) { setError(t("choose_type_required")); return; }
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else if (mode === "register") {
        await register({ ...form, username: form.username.trim().toLowerCase(), ref: refParam, lang });
        localStorage.removeItem("deuro_ref");
        navigate("/");
        return;
      } else if (mode === "forgot") {
        const data = await forgotPassword(form.email, lang);
        setResetLink(data.reset_link || "sent");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const isBiz = form.user_type === "attivita";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#fdfbf7]" data-testid="auth-page">
      <div className="relative hidden lg:flex flex-col justify-between p-12 grain overflow-hidden" style={{ background: "var(--primary)" }}>
        <img src="/hero-home.webp" alt="" width="1264" height="848" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative z-10 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-[#fdfbf7]" data-testid="auth-home-link">
            <img src="/logo-192.png" alt="Deuro" width="44" height="44" className="w-11 h-11 rounded-xl object-cover" />
            <span className="font-head font-extrabold text-2xl tracking-tight">Deuro</span>
          </button>
        </div>
        <div className="relative z-10 max-w-md">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-head text-5xl font-extrabold text-[#fdfbf7] leading-[1.05] tracking-tight">
            {t("home_title")}
          </motion.h1>
          <p className="mt-6 text-lg text-[#c9d3cb] leading-relaxed">{t("home_lead")}</p>
          <div className="mt-10 flex flex-col gap-4 text-[#e6ece7]">
            <Feature icon={<MapPin className="w-5 h-5" />} text={t("feat1")} />
            <Feature icon={<Repeat className="w-5 h-5" />} text={t("feat2")} />
            <Feature icon={<Wallet className="w-5 h-5" />} text={t("feat3")} />
          </div>
        </div>
        <div className="relative z-10 text-sm text-[#9fb0a4]">{t("tagline")}</div>
      </div>

      <div className="flex flex-col p-6 sm:p-12">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="auth-home-link-mobile">
            <img src="/logo-192.png" alt="Deuro" width="36" height="36" className="w-9 h-9 rounded-xl object-cover" />
            <span className="font-head font-extrabold text-xl lg:hidden">Deuro</span>
            <span className="hidden lg:inline text-sm font-medium text-[#5f6b62] hover:text-[#1e3326]">← {t("nav_home")}</span>
          </button>
          <LanguageSwitcher />
        </div>

        <div className="flex-1 flex items-center justify-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md py-8">
            <h2 className="font-head text-3xl font-bold tracking-tight">
              {mode === "login" ? t("welcome_back") : mode === "register" ? t("create_account") : t("reset_title")}
            </h2>
            <p className="text-[#5f6b62] mt-2">{mode === "login" ? t("login_sub") : mode === "register" ? t("register_sub") : t("reset_sub")}</p>

            {mode !== "forgot" && (
              <div className="mt-8" data-testid="google-auth-section">
                <GoogleSignInButton label={t("google_continue")} />
                <div className="my-5 flex items-center gap-3" role="separator" data-testid="auth-method-divider">
                  <span className="h-px flex-1 bg-[#e5dfd3]" />
                  <span className="text-xs font-medium text-[#5f6b62]">{t("or_with_email")}</span>
                  <span className="h-px flex-1 bg-[#e5dfd3]" />
                </div>
              </div>
            )}

            <form onSubmit={submit} className={`${mode === "forgot" ? "mt-8" : ""} space-y-4`} data-testid="auth-form">
              {mode === "register" && (
                <>
                  {refParam && <div className="text-sm bg-[#d1fae5] text-[#065f46] rounded-lg px-3 py-2" data-testid="ref-banner">{t("ref_invited_by").replace("{name}", refParam)}</div>}
                  <div>
                    <span className="text-sm font-medium text-[#1b241e]">{t("choose_type_required")}</span>
                    <div className="grid grid-cols-2 gap-3 mt-1.5">
                      <button type="button" onClick={() => set("user_type", "privato")} data-testid="type-privato"
                        className={`py-3 rounded-xl border-2 font-medium transition-all ${form.user_type === "privato" ? "border-[#1e3326] bg-[#1e3326] text-white" : "border-[#e5dfd3] hover:border-[#1e3326]"}`}>
                        {t("type_privato")}
                      </button>
                      <button type="button" onClick={() => set("user_type", "attivita")} data-testid="type-attivita"
                        className={`py-3 rounded-xl border-2 font-medium transition-all ${form.user_type === "attivita" ? "border-[#d97706] bg-[#d97706] text-white" : "border-[#e5dfd3] hover:border-[#d97706]"}`}>
                        {t("type_attivita")}
                      </button>
                    </div>
                  </div>
                  <Field label={t("name")}>
                    <input data-testid="register-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="deuro-input" />
                  </Field>
                  <Field label={t("username_label")}>
                    <div className="relative">
                      <AtSign className="w-4 h-4 text-[#5f6b62] absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input data-testid="register-username" required minLength={3} maxLength={20} pattern="[a-zA-Z0-9_.]{3,20}" value={form.username}
                        onChange={(e) => set("username", e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, ""))} className="deuro-input pl-10 font-mono" placeholder="mario83" />
                    </div>
                    <p className="text-xs text-[#5f6b62] mt-1">{t("username_hint")}</p>
                  </Field>
                  {form.user_type && (
                    <div className="space-y-4 p-4 rounded-xl bg-[#f4f0e8]/70 border border-[#e5dfd3]">
                      {isBiz ? (
                        <>
                          <Field label={t("category_label")}><input data-testid="register-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="deuro-input" placeholder={t("category_ph")} /></Field>
                          <Field label={t("services_label")}><TagInput value={form.services} onChange={(v) => set("services", v)} testid="register-services" color="var(--accent)" /></Field>
                          <Field label={t("products_label")}><TagInput value={form.products} onChange={(v) => set("products", v)} testid="register-products" color="var(--accent)" /></Field>
                        </>
                      ) : (
                        <>
                          <Field label={t("skills_label")}><TagInput value={form.skills} onChange={(v) => set("skills", v)} testid="register-skills" /></Field>
                          <Field label={t("services_label")}><TagInput value={form.services} onChange={(v) => set("services", v)} testid="register-services" /></Field>
                        </>
                      )}
                    </div>
                  )}
                  <Field label={t("city")}>
                    <input data-testid="register-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="deuro-input" />
                  </Field>
                  <button type="button" onClick={useLocationBtn} data-testid="use-location-btn"
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-[#e5dfd3] text-[#5f6b62] hover:border-[#059669] hover:text-[#059669] transition-all text-sm font-medium">
                    <MapPin className="w-4 h-4" /> {form.lat ? t("location_set") : t("use_location")}
                  </button>
                  <p className="text-xs leading-relaxed text-center text-[#5f6b62] px-2" data-testid="register-location-explanation">
                    {t("register_location_explanation")}
                  </p>
                </>
              )}
              <Field label={t("email")}>
                <input data-testid="auth-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="deuro-input" placeholder="tu@email.it" />
              </Field>
              {mode !== "forgot" && (
                <Field label={t("password")}>
                  <PasswordInput inputTestId="auth-password" toggleTestId="auth-password-toggle" showLabel={t("show_password")} hideLabel={t("hide_password")}
                    required minLength={mode === "register" ? 8 : undefined} value={form.password} onChange={(e) => set("password", e.target.value)} className="deuro-input" placeholder="••••••••" autoComplete={mode === "login" ? "current-password" : "new-password"} />
                </Field>
              )}
              {mode === "register" && (
                <p className="text-xs text-[#065f46] bg-[#d1fae5] rounded-lg px-3 py-2" data-testid="free-signup-note">{t("free_signup_note")}</p>
              )}
              {mode === "login" && (
                <div className="text-right -mt-1">
                  <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); setResetLink(""); }}
                    className="text-sm font-medium text-[#d97706] hover:underline">{t("forgot_password")}</button>
                </div>
              )}

              {error && error !== "—" && <div data-testid="auth-error" className="text-sm text-[#d9532f] bg-[#fdeae5] border border-[#f3c9bd] rounded-lg px-3 py-2">{error}</div>}

              {mode === "forgot" && resetLink && (
                <div className="text-sm bg-[#d1fae5] border border-[#a7e8cf] rounded-lg px-3 py-3" data-testid="reset-link-box">
                  <p className="text-[#065f46] mb-2">{resetLink === "sent" ? t("reset_email_sent") : t("reset_link_ready")}</p>
                  {resetLink !== "sent" && <a href={resetLink} data-testid="reset-link" className="font-semibold text-[#059669] break-all underline">{resetLink}</a>}
                </div>
              )}

              {!(mode === "forgot" && resetLink) && (
                <button type="submit" disabled={loading} data-testid="auth-submit"
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[#1e3326] text-white font-semibold hover:bg-[#284635] active:scale-[0.99] transition-all disabled:opacity-60">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{mode === "login" ? t("login") : mode === "register" ? t("register") : t("send_reset_link")} <ArrowRight className="w-4 h-4" /></>}
                </button>
              )}
            </form>

            {mode === "forgot" ? (
              <p className="mt-6 text-center text-sm text-[#5f6b62]">
                <button data-testid="back-to-login" onClick={() => { setMode("login"); setError(""); setResetLink(""); }} className="font-semibold text-[#d97706] hover:underline">{t("back_to_login")}</button>
              </p>
            ) : (
              <p className="mt-6 text-center text-sm text-[#5f6b62]">
                {mode === "login" ? t("no_account") : t("have_account")}{" "}
                <button data-testid="toggle-auth-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="font-semibold text-[#d97706] hover:underline">
                  {mode === "login" ? t("register") : t("login")}
                </button>
              </p>
            )}

            <div className="mt-8 border-t border-[#e5dfd3] pt-5">
              <button onClick={() => setShowAbout((s) => !s)} data-testid="about-toggle" className="flex items-center gap-2 text-sm font-semibold text-[#1e3326]">
                <Info className="w-4 h-4 text-[#d97706]" /> {t("about_title")}
              </button>
              {showAbout && (
                <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="text-sm text-[#5f6b62] leading-relaxed mt-3" data-testid="about-text">
                  {t("home_what_p")}
                </motion.p>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center text-[#f59e0b]">{icon}</div>
      <span>{text}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="block">
      <span className="text-sm font-medium text-[#1b241e]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
